import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Fetch (and cache in memory) the site settings JSON.
 * Consumers receive `null` while loading, then a merged settings object.
 * Emits a window event 'wvt:site-settings:updated' to allow invalidation from the admin panel.
 */

let _cache = null;
let _inflight = null;
const _listeners = new Set();

async function fetchSettings() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      _cache = await api.siteSettings();
    } catch (e) {
      console.warn("Failed to load site settings, using defaults", e);
      _cache = {};
    } finally {
      _inflight = null;
    }
    _listeners.forEach((l) => l(_cache));
    return _cache;
  })();
  return _inflight;
}

export function invalidateSiteSettings(fresh) {
  if (fresh !== undefined) _cache = fresh;
  else _cache = null;
  _listeners.forEach((l) => l(_cache));
}

export default function useSiteSettings() {
  const [settings, setSettings] = useState(_cache);

  useEffect(() => {
    let mounted = true;
    if (!_cache) fetchSettings().then((s) => { if (mounted) setSettings(s); });
    const listener = (s) => { if (mounted) setSettings(s); };
    _listeners.add(listener);
    return () => { mounted = false; _listeners.delete(listener); };
  }, []);

  return settings;
}
